import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtTime, idemKey } from "@/lib/api";
import { DemoBadge, Empty, ErrorBox, PageHead, Spinner, useFetch } from "@/components/common";

const manilaDate = (offset) => {
  const d = new Date(Date.now() + offset * 86400000);
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Manila" }).format(d);
};
const DAYS = Array.from({ length: 14 }, (_, i) => manilaDate(i));

const Chip = ({ active, children, ...p }) => (
  <button type="button" {...p} className={`rounded-xl border px-3 py-2 text-xs font-semibold transition-colors disabled:opacity-40 ${active ? "border-purple-600 bg-purple-600 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-purple-300"}`}>{children}</button>
);

function SlotPicker({ providerId, value, onChange }) {
  const [day, setDay] = useState(DAYS[1]);
  const { data, loading } = useFetch(providerId ? `/customer/slots?provider_id=${providerId}&date=${day}` : null);
  return (
    <div className="space-y-3">
      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
        {DAYS.map((d) => (
          <Chip key={d} active={d === day} onClick={() => { setDay(d); onChange(""); }} data-testid={`book-day-${d}`}>
            {new Date(`${d}T00:00:00`).toLocaleDateString("en-PH", { weekday: "short", day: "numeric" })}
          </Chip>
        ))}
      </div>
      {loading ? <Spinner label="Loading schedule" /> : data?.length === 0 ? (
        <p className="text-xs text-slate-500">No schedule on this day.</p>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {data?.map((s) => (
            <Chip key={s.start} active={value === s.start} disabled={!s.available} onClick={() => onChange(s.start)} data-testid="book-slot">{fmtTime(s.start)}</Chip>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Book() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const ents = useFetch("/customer/entitlements");
  const [entId, setEntId] = useState(params.get("benefit") || "");
  const [benId, setBenId] = useState("self");
  const [provId, setProvId] = useState("");
  const [slot, setSlot] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [key] = useState(idemKey);

  const bookable = (ents.data || []).filter((e) => e.requires_booking && !e.expired && (e.remaining_qty == null || e.remaining_qty - e.pending_requests > 0));
  const ent = bookable.find((e) => e.id === entId);
  const provs = useFetch(ent ? `/customer/providers?entitlement_id=${ent.id}` : null);
  const people = useMemo(() => [{ id: "self", full_name: user.full_name, relationship: "SELF" }, ...(user.beneficiaries || [])]
    .filter((p) => ent?.eligible_relationships.includes(p.relationship)), [user, ent]);

  useEffect(() => { if (provs.data?.length === 1) setProvId(provs.data[0].id); }, [provs.data]);

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post("/customer/service-requests", { entitlement_id: entId, beneficiary_id: benId, provider_id: provId, scheduled_at: slot, reason, idempotency_key: key });
      toast.success("Request submitted");
      nav("/app/activity");
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  if (ents.loading) return <Spinner />;
  return (
    <div className="space-y-6">
      <PageHead eyebrow="Book services" title="Request a service" />
      {!user.consent && (
        <div className="rounded-xl bg-amber-50 p-3 text-xs text-amber-900" data-testid="consent-required-notice">
          Please accept the privacy & data consent in your <Link to="/app/profile" className="font-bold underline">Profile</Link> before booking.
        </div>
      )}
      {bookable.length === 0 ? <Empty title="No bookable benefits" hint="Activate a package or check your remaining balances." /> : (
        <>
          <section className="space-y-2">
            <p className="label">1. Service</p>
            {bookable.map((e) => (
              <button key={e.id} type="button" onClick={() => { setEntId(e.id); setBenId("self"); setProvId(""); setSlot(""); }} data-testid={`book-service-${e.benefit_code}`}
                className={`flex w-full items-center justify-between rounded-2xl border p-3.5 text-left transition-colors ${entId === e.id ? "border-purple-600 bg-purple-50" : "border-slate-200 bg-white"}`}>
                <span><span className="block text-sm font-bold">{e.benefit_name}</span>
                  <span className="text-xs text-slate-500">{e.delivery_mode === "ONLINE" ? "Online" : "In person"} · {e.remaining_qty == null ? "Unlimited" : `${e.remaining_qty - e.pending_requests} available`}</span></span>
                {e.is_demo ? <DemoBadge /> : entId === e.id && <CheckCircle2 className="text-purple-600" size={18} />}
              </button>
            ))}
          </section>
          {ent && (
            <>
              <section className="space-y-2">
                <p className="label">2. Who is this for?</p>
                <div className="flex flex-wrap gap-2">
                  {people.map((p) => <Chip key={p.id} active={benId === p.id} onClick={() => setBenId(p.id)} data-testid={`book-beneficiary-${p.id}`}>{p.full_name} · {p.relationship === "SELF" ? "Me" : p.relationship.toLowerCase()}</Chip>)}
                </div>
                {ent.eligible_relationships.length > 1 && <Link to="/app/profile" className="text-[11px] font-semibold text-purple-700">+ Add family members in Profile</Link>}
              </section>
              <section className="space-y-2">
                <p className="label">3. Provider</p>
                <div className="flex flex-wrap gap-2">
                  {provs.data?.map((p) => <Chip key={p.id} active={provId === p.id} onClick={() => { setProvId(p.id); setSlot(""); }} data-testid={`book-provider-${p.id}`}>{p.name}</Chip>)}
                </div>
              </section>
              {provId && (
                <section className="space-y-2"><p className="label">4. Schedule</p><SlotPicker providerId={provId} value={slot} onChange={setSlot} /></section>
              )}
              <section className="space-y-2">
                <p className="label">Reason (optional)</p>
                <textarea className="field" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Briefly describe your concern" data-testid="book-reason-input" />
              </section>
              <ErrorBox error={error} />
              <button className="btn-primary w-full py-3" disabled={!slot || !provId || busy} onClick={submit} data-testid="book-submit-button">
                {busy && <Loader2 className="animate-spin" size={16} />} Submit request
              </button>
              <p className="text-center text-[11px] text-slate-400">Your benefit balance is deducted only after the service is completed.</p>
            </>
          )}
        </>
      )}
    </div>
  );
}
